import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { Table, TableWrapper } from "@/components/ui/table";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { PageHeader } from "@/components/data-display/page-header";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";

/**
 * TRS-03 Route Comparison — layout port of the uat prototype
 * (`.design-ref/prototype/app/(enterprise-wallet)/treasury/routes/page.tsx` and
 * `features/treasury/treasury.tsx#RouteComparison`): back link, page header,
 * intent panel, the route comparison table with its eight canonical columns,
 * and the policy note banner.
 *
 * No treasury routing backend exists in this environment (page-delivery-pipeline.md
 * G6), so the prototype's intent summary, route rows, APY/fee figures and policy
 * checks are deliberately NOT rendered: the intent fields show the shared
 * unavailable marker, the table shows its designed empty state, and every control
 * that would need the route-execution or policy backend is disabled with the
 * shared WIP reason. No sample data is copied from treasury-data.ts.
 */

const WIP_ACTION_HINT_ID = "treasury-routes-wip-action-hint";

const COLUMNS = [
  { key: "route", label: "Route" },
  { key: "provider", label: "Provider" },
  { key: "apy", label: "APY" },
  { key: "fee", label: "Fee" },
  { key: "lock", label: "Lock Period" },
  { key: "risk", label: "Risk" },
  { key: "policy", label: "Policy" },
] as const;

export default function TreasuryRoutesPage() {
  return (
    <div>
      {/* Reason shared by every disabled control below, referenced via aria-describedby. */}
      <span id={WIP_ACTION_HINT_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>

      <Breadcrumb
        items={[{ label: "Treasury", href: "/treasury" }, { label: "Route Comparison" }]}
      />

      <PageHeader
        title="Route Comparison"
        subtitle="Compare execution routes for a treasury intent across custody, exchange, OTC/RFQ, and DeFi providers."
        actions={<WipHint />}
      />

      <p className="mb-5 text-sm text-muted">
        Route comparison has no backend in this environment yet, so no route, provider, yield, fee
        or policy result is shown.
      </p>

      {/* Intent panel — prototype `Panel` shape (bordered section with a header). */}
      <section className="mb-5 overflow-hidden rounded-lg border border-line bg-surface">
        <header className="border-b border-line px-5 py-3">
          <h2 className="text-[15px] font-bold text-ink">Intent</h2>
        </header>
        <dl className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
              Intent
            </dt>
            <dd className="mt-1 text-[15px] font-bold text-ink">
              <UnavailableValue />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
              Source wallet
            </dt>
            <dd className="mt-1 text-sm">
              <UnavailableValue />
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
              Comparing
            </dt>
            <dd className="mt-1 text-sm">
              <UnavailableValue />
            </dd>
          </div>
        </dl>
      </section>

      <TableWrapper className="mb-5">
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              {COLUMNS.map((column) => (
                <th key={column.key} scope="col" className="px-4 py-2.5">
                  {column.label}
                </th>
              ))}
              <th scope="col" className="px-4 py-2.5 text-right">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={COLUMNS.length + 1}
              title="Route comparison is not available in this environment yet"
              description="Comparing execution routes, APY, fees, and policy checks across providers requires the treasury routing backend, which does not exist in this environment yet."
            />
          </tbody>
        </Table>
      </TableWrapper>

      <InfoBanner variant="info">
        <strong>Policy note:</strong> each route is evaluated against the active transfer policy
        before it can be signed.{" "}
        <button
          type="button"
          disabled
          title={WIP_ACTION_HINT}
          aria-describedby={WIP_ACTION_HINT_ID}
          className="font-semibold underline disabled:cursor-not-allowed disabled:no-underline"
        >
          View policy →
        </button>
      </InfoBanner>
    </div>
  );
}
